import { BlockchainDetailsModel } from 'src/common/api/organizations'
import { SnapshotModel } from 'src/common/api/reportsSnapshot'
import {
  ClaimStatus,
  DailySnapshot,
  EarningStatement,
  EligibleWallet,
  MintStatus,
  PortalMetrics,
  EarningPortalData,
  RevSharingWallet,
  SnapshotStatus,
  WALLET_TYPE
} from 'src/common/api/partnerPayouts'
import { PartnerRevenueModel } from 'src/common/api/partnerRevenue'
import { OrderDetails } from 'src/common/api/orders'
import { ChainName } from 'src/common/static'
import { formatTime } from 'src/common/helpers/functions'

export const DEFAULT_RATE = 0.01 // 1.00% - no customer-facing rate source yet

const isCurrentlyEligible = (w: BlockchainDetailsModel): boolean => !!w.isSnapshot

const walletFromSnapshotIdentity = (id: string, snapshots: SnapshotModel[]): BlockchainDetailsModel => {
  const snap = snapshots.find((s) => s.blockchainDetailsId === id)
  return {
    id,
    name: snap?.walletName || id,
    address: snap?.walletAddress || id,
    chain: (snap?.walletChain as ChainName) || ChainName.ETHEREUM,
    walletType: snap?.walletType || WALLET_TYPE.TREASURY,
    isSnapshot: false
  } as BlockchainDetailsModel
}

const fmtDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })

const DAY_MS = 86400000

const toNum = (value: string): number => {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}
const SNAPSHOT_DEFAULT_TIME = '04:00 PM'
const snapshotTimeOrDefault = (value: string | undefined): string => {
  if (!value) return SNAPSHOT_DEFAULT_TIME
  const d = new Date(value)
  return isNaN(d.getTime()) ? SNAPSHOT_DEFAULT_TIME : formatTime(value)
}

// Inclusive day count for the selected [fromDate, toDate] window (YYYY-MM-DD).
const periodDaysBetween = (fromDate: string, toDate: string): number => {
  const start = Date.parse(`${fromDate}T00:00:00Z`)
  const end = Date.parse(`${toDate}T00:00:00Z`)
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return 1
  return Math.round((end - start) / DAY_MS) + 1
}

// Only wallets flagged "Eligible for Revenue Accrual" (isSnapshot) are eligible for the settlement ledger.
export const eligibleTreasuryWallets = (wallets: BlockchainDetailsModel[]): BlockchainDetailsModel[] =>
  wallets.filter((w) => !!w.isSnapshot)

interface BuildPortalDataArgs {
  wallets: BlockchainDetailsModel[]
  snapshots: SnapshotModel[]
  effectiveRate: number
  fromDate: string
  toDate: string
}

export const buildPortalData = ({
  wallets,
  snapshots,
  effectiveRate: fallbackRate,
  fromDate,
  toDate
}: BuildPortalDataArgs): EarningPortalData => {
  const periodDays = periodDaysBetween(fromDate.slice(0, 10), toDate.slice(0, 10))

  const snapRateDecimal = (s: SnapshotModel): number => {
    const r = Number(s.rate)
    return Number.isFinite(r) ? r / 100 : fallbackRate
  }

  const snapWalletIds = new Set(snapshots.map((s) => s.blockchainDetailsId))
  const currentWallets = wallets.filter(isCurrentlyEligible)
  const currentIds = new Set(currentWallets.map((w) => w.id))
  const historicalWallets = wallets.filter((w) => !currentIds.has(w.id) && snapWalletIds.has(w.id))
  const knownIds = new Set(wallets.map((w) => w.id))
  const orphanWallets = Array.from(snapWalletIds)
    .filter((id) => !knownIds.has(id))
    .map((id) => walletFromSnapshotIdentity(id, snapshots))
  const displayWallets = [...currentWallets, ...historicalWallets, ...orphanWallets]

  const eligibleWallets: EligibleWallet[] = displayWallets.map((wallet) => {
    const walletSnaps = snapshots
      .filter((s) => s.blockchainDetailsId === wallet.id)
      .slice()
      .sort((a, b) => (a.consolidationDate < b.consolidationDate ? -1 : 1))

    let cumulative = 0
    const dailySnapshots: DailySnapshot[] = walletSnaps.map((s) => {
      const balanceQcad = toNum(s.balance)
      const rate = snapRateDecimal(s)
      const dailyRevenueQcad = (balanceQcad * rate) / 365
      cumulative += dailyRevenueQcad
      return {
        date: fmtDate(s.consolidationDate),
        status: SnapshotStatus.ACTIVE,
        snapshotTime: snapshotTimeOrDefault(s.consolidationDate),
        balanceQcad,
        effectiveRate: rate,
        dailyRevenueQcad,
        cumulativeAccrualQcad: cumulative
      }
    })

    const daysEligible = dailySnapshots.length
    // Avg. Daily Balance = Σ(EOD balances) ÷ days the wallet actually had a snapshot (0 when none).
    const sumBalances = dailySnapshots.reduce((sum, s) => sum + s.balanceQcad, 0)
    const avgDailyBalanceQcad = daysEligible > 0 ? sumBalances / daysEligible : 0
    const grossAccrualQcad = dailySnapshots.reduce((sum, s) => sum + s.dailyRevenueQcad, 0)
    // Surface the wallet's most recently applied rate (latest snapshot) at a glance.
    const walletEffectiveRate = dailySnapshots.length
      ? dailySnapshots[dailySnapshots.length - 1].effectiveRate
      : fallbackRate

    return {
      id: wallet.id,
      name: wallet.name || wallet.id,
      address: wallet.address,
      chain: wallet.chain,
      walletType: wallet.walletType as WALLET_TYPE,
      daysEligible,
      periodDays,
      prorated: daysEligible > 0 && daysEligible < periodDays,
      avgDailyBalanceQcad,
      grossAccrualQcad,
      effectiveRate: walletEffectiveRate,
      isRevSharingWallet: false,
      previouslyEligible: !isCurrentlyEligible(wallet),
      snapshots: dailySnapshots
    }
  })

  // Pooled Avg. Daily Balance = Σ(all snapshot balances) ÷ Σ(days eligible across wallets).
  const pooledBalances = eligibleWallets.reduce(
    (sum, w) => sum + w.snapshots.reduce((acc, d) => acc + d.balanceQcad, 0),
    0
  )
  const pooledDays = eligibleWallets.reduce((sum, w) => sum + w.daysEligible, 0)
  const avgDailyBalanceMtdQcad = pooledDays > 0 ? pooledBalances / pooledDays : 0
  // No expense backend, so net revenue = gross accrual (nothing to deduct).
  const netRevenuePayoutMtdQcad = eligibleWallets.reduce((sum, w) => sum + w.grossAccrualQcad, 0)

  const latestSnapshot = snapshots.reduce<SnapshotModel | null>(
    (latest, s) => (!latest || s.consolidationDate > latest.consolidationDate ? s : latest),
    null
  )
  const effectiveRate = latestSnapshot ? snapRateDecimal(latestSnapshot) : fallbackRate

  const metrics: PortalMetrics = {
    currentPeriodLabel: `${fmtDate(fromDate)} – ${fmtDate(toDate).replace(/, \d{4}$/, '')}`,
    periodDays,
    netRevenuePayoutMtdQcad,
    effectiveRate,
    avgDailyBalanceMtdQcad,
    unclaimed: null
  }

  return { metrics, eligibleWallets, statements: [], auditLog: [] }
}

// "2026-02-01" -> "Feb 2026" (UTC so the stored first-of-month doesn't roll back a day).
const fmtPeriod = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' })

// createdAt / claimedAt -> "Mar 5, 2026 — 09:14 UTC"
const fmtTimestamp = (iso: string): string => {
  const date = new Date(iso)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
}

// Map the partner-facing settlement records into the Earnings statements view-model. The backend already
// filters to this partner's visible records; the rev-sharing wallet is the org's isRevenue wallet.
// revOrderMap holds each statement's mint order (keyed by revenue id) so claimed rows can show mint progress.
export const mapPartnerRevenueToStatements = (
  revenue: PartnerRevenueModel[],
  revSharingWallet: RevSharingWallet,
  revOrderMap: Map<string, OrderDetails> = new Map()
): EarningStatement[] =>
  revenue.map((r) => {
    const order = revOrderMap?.get(r.id)
    return {
      id: r.id,
      period: fmtPeriod(r.consolidationMonth),
      statementDate: fmtTimestamp(r.createdAt),
      scheduledMintDate: '',
      daysInPeriod: 0,
      avgDailyBalanceQcad: toNum(r.avgBalance ?? '0'),
      effectiveRate: toNum(r.avgRate ?? '0') / 100,
      grossAccrualQcad: toNum(r.grossAccrual ?? '0'),
      allowableExpensesQcad: toNum(r.allowableExpenses),
      netRevenuePayoutQcad: toNum(r.netPayout ?? '0'),
      claimStatus: r.isClaimed ? ClaimStatus.CLAIMED : ClaimStatus.UNCLAIMED,
      claimedAt: r.claimedAt ? fmtTimestamp(r.claimedAt) : null,
      mintStatus: (order?.status?.toLowerCase() as MintStatus) ?? null,
      mintTxHash: null,
      mintTxDate: order?.updatedAt ? fmtTimestamp(order.updatedAt) : null,
      mintTxChain: order?.chain as ChainName | undefined,
      revSharingWallet,
      pdfAvailable: !!r.documentId,
      comments: r.comments ?? null,
      claimedBy: r.claimedBy ?? null,
      updatedAt: r.updatedAt
    }
  })
